/**
 * Sticky Product Steps — Scrub-Linked ScrollTrigger
 *
 * Product image stays pinned (CSS sticky) while step cards
 * scroll vertically. Each step smoothly fades in/out via
 * GSAP scrub timelines tied directly to scroll position —
 * no callbacks, no duration-based tweens, just buttery
 * scroll-linked animation.
 *
 * Product Spin mode: 4 product images cycle as the user
 * scrolls, creating a pseudo-360° rotation effect.
 *
 * Requires GSAP + ScrollTrigger (loaded by the section).
 */
(function () {
  'use strict';

  function waitForDeps(cb) {
    if (window.gsap && window.ScrollTrigger) return cb();
    var check = setInterval(function () {
      if (window.gsap && window.ScrollTrigger) {
        clearInterval(check);
        cb();
      }
    }, 50);
    setTimeout(function () { clearInterval(check); }, 8000);
  }

  function initStickySteps(sectionId) {
    var section = document.querySelector('[data-sticky-steps-id="' + sectionId + '"]');
    if (!section) return;

    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var animStyle = section.dataset.stepAnimation || 'fade_up';
    var spinEnabled = section.dataset.spinEnabled === 'true';

    gsap.registerPlugin(ScrollTrigger);

    var steps = section.querySelectorAll('.hp-sticky-steps__step');
    var mainImage = section.querySelector('.hp-sticky-steps__image--main');
    var stepImages = section.querySelectorAll('.hp-sticky-steps__image--swap');
    var spinImages = section.querySelectorAll('.hp-sticky-steps__image--spin');
    var triggers = [];

    if (steps.length === 0) return;

    var totalSteps = steps.length;

    /* ── Animation presets ── */
    function getFromProps() {
      switch (animStyle) {
        case 'fade_up':    return { opacity: 0.12, y: 30 };
        case 'fade_in':    return { opacity: 0.12 };
        case 'slide_left': return { opacity: 0.12, x: 50 };
        default:           return { opacity: 0.12, y: 30 };
      }
    }

    function getToProps() {
      switch (animStyle) {
        case 'fade_up':    return { opacity: 1, y: 0 };
        case 'fade_in':    return { opacity: 1 };
        case 'slide_left': return { opacity: 1, x: 0 };
        default:           return { opacity: 1, y: 0 };
      }
    }

    function getExitProps() {
      switch (animStyle) {
        case 'fade_up':    return { opacity: 0.12, y: -15 };
        case 'fade_in':    return { opacity: 0.12 };
        case 'slide_left': return { opacity: 0.12, x: -30 };
        default:           return { opacity: 0.12, y: -15 };
      }
    }

    /* ══════════════════════════════════════════
     *  PRODUCT SPIN — scroll-linked instant swap
     * ══════════════════════════════════════════ */

    function initSpin() {
      if (!spinEnabled || spinImages.length < 2) return;

      var totalImages = spinImages.length;
      var currentIdx = 0;

      /* Show first, hide rest */
      for (var i = 0; i < spinImages.length; i++) {
        spinImages[i].style.opacity = i === 0 ? '1' : '0';
      }

      var spinTrigger = ScrollTrigger.create({
        trigger: section,
        start: 'top 80%',
        end: 'bottom 20%',
        onUpdate: function (self) {
          var idx = Math.min(
            Math.floor(self.progress * totalImages),
            totalImages - 1
          );
          if (idx < 0) idx = 0;
          if (idx !== currentIdx) {
            spinImages[currentIdx].style.opacity = '0';
            spinImages[idx].style.opacity = '1';
            currentIdx = idx;
          }
        }
      });
      triggers.push(spinTrigger);
    }

    /* ── Image crossfade (non-spin mode) ── */
    function activateStepImage(stepIdx) {
      if (spinEnabled || stepImages.length === 0) return;

      var target = null;
      stepImages.forEach(function (img) {
        if (parseInt(img.dataset.stepIndex, 10) === stepIdx) {
          target = img;
        }
      });

      if (target) {
        if (mainImage) gsap.to(mainImage, { opacity: 0, duration: 0.4, ease: 'power2.inOut', overwrite: true });
        stepImages.forEach(function (img) {
          gsap.to(img, {
            opacity: img === target ? 1 : 0,
            duration: 0.4,
            ease: 'power2.inOut',
            overwrite: true
          });
        });
      } else {
        showMainImage();
      }
    }

    function showMainImage() {
      if (spinEnabled) return;
      if (mainImage) gsap.to(mainImage, { opacity: 1, duration: 0.4, ease: 'power2.inOut', overwrite: true });
      stepImages.forEach(function (img) {
        gsap.to(img, { opacity: 0, duration: 0.4, ease: 'power2.inOut', overwrite: true });
      });
    }

    /* ── Init spin if enabled ── */
    initSpin();

    /* ══════════════════════════════════════════
     *  SCRUB-BASED STEP ANIMATIONS
     *
     *  Each step gets a timeline scrubbed to scroll:
     *  ┌──────────┬──────────┬──────────┐
     *  │  ENTER   │   HOLD   │   EXIT   │
     *  │ (0 → 1)  │ (1 → 2)  │ (2 → 3)  │
     *  └──────────┴──────────┴──────────┘
     *  Last step skips EXIT phase so it stays visible.
     * ══════════════════════════════════════════ */

    steps.forEach(function (step, idx) {
      var stepIdx = parseInt(step.dataset.stepIndex, 10) || idx;
      var isLast = idx === totalSteps - 1;

      if (reducedMotion) {
        gsap.set(step, { opacity: 1, y: 0, x: 0 });
        var st = ScrollTrigger.create({
          trigger: step,
          start: 'top 65%',
          end: 'bottom 35%',
          onEnter: function () {
            step.classList.add('is-active');
            activateStepImage(stepIdx);
          },
          onLeave: function () { step.classList.remove('is-active'); },
          onEnterBack: function () {
            step.classList.add('is-active');
            activateStepImage(stepIdx);
          },
          onLeaveBack: function () { step.classList.remove('is-active'); }
        });
        triggers.push(st);
        return;
      }

      /* Set initial hidden state */
      gsap.set(step, getFromProps());

      /* Build scrub timeline */
      var from = getFromProps();
      var to = getToProps();
      var exit = getExitProps();

      var tl = gsap.timeline({
        scrollTrigger: {
          trigger: step,
          start: 'top 85%',
          end: isLast ? 'top 30%' : 'bottom 15%',
          scrub: 0.5,
          onEnter: function () {
            step.classList.add('is-active');
            activateStepImage(stepIdx);
          },
          onLeave: function () { step.classList.remove('is-active'); },
          onEnterBack: function () {
            step.classList.add('is-active');
            activateStepImage(stepIdx);
          },
          onLeaveBack: function () { step.classList.remove('is-active'); }
        }
      });

      if (isLast) {
        /* Last step: just fade in, stay visible */
        tl.fromTo(step, from,
          Object.assign({}, to, { duration: 1, ease: 'none' }), 0);
      } else {
        /* Full cycle: enter → hold → exit */
        tl.fromTo(step, from,
          Object.assign({}, to, { duration: 1, ease: 'none' }), 0);
        tl.to(step,
          Object.assign({}, exit, { duration: 1, ease: 'none' }), 2);
      }

      triggers.push(tl.scrollTrigger);
    });

    /* ── Cleanup for Shopify editor ── */
    section._stickyStepsCleanup = function () {
      triggers.forEach(function (st) { st.kill(); });
      triggers = [];
    };
  }

  /* Boot */
  function boot() {
    document.querySelectorAll('[data-sticky-steps-id]').forEach(function (el) {
      var id = el.dataset.stickyStepsId;
      if (!el._stickyStepsInit) {
        el._stickyStepsInit = true;
        waitForDeps(function () { initStickySteps(id); });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Shopify editor events */
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('[data-sticky-steps-id]');
    if (el) {
      el._stickyStepsInit = false;
      waitForDeps(function () {
        initStickySteps(el.dataset.stickyStepsId);
        ScrollTrigger.refresh();
      });
    }
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target.querySelector('[data-sticky-steps-id]');
    if (el && el._stickyStepsCleanup) el._stickyStepsCleanup();
  });
})();
